// toggleActivityReaction.js

import { EngagementType } from 'utilities/Enums';
import { generateKey } from 'utilities/Keys';
import { resolveBaseKey, withBaseKey } from 'utilities/basePersistence';
import { buildUserSummary } from 'utilities/Users';
import {
  createEngagement,
  deleteEngagementByKey,
  updateEngagement,
} from 'services/engagements/EngagementWriteService';
import { nowTimestamp } from 'services/nodes/NodeWriteService';
import {
  ACTIVITY_REACTION_DEFAULT,
  getActivityCommentKey,
  getActivityCommentTargetKey,
  getActivityEventKey,
  getActivityReactionTargetKey,
  normalizeActivityReactionKey,
} from 'node/activity/utils/activityReactions';

/**
 * @param {object|null|undefined} row
 * @returns {string}
 */
function reactionKeyForRow(row) {
  if (!row) return '';
  if (row.type === EngagementType.COMMENT_LIKE) return 'heart';
  return normalizeActivityReactionKey(row.reaction);
}

/**
 * Create, switch, or clear the current user's reaction on an event or comment.
 *
 * @param {object} params
 * @param {object|null} [params.existing] - Current user's reaction doc.
 * @param {object|null} [params.legacyLike] - Leftover COMMENT_LIKE to retire.
 * @param {string} [params.reaction]
 * @param {object} [params.event]
 * @param {object} [params.comment]
 * @param {object} params.node
 * @param {object} params.user
 * @returns {Promise<void>}
 */
export async function toggleActivityReaction({
  existing = null,
  legacyLike = null,
  reaction = ACTIVITY_REACTION_DEFAULT,
  event,
  comment,
  node,
  user,
} = {}) {
  const next = normalizeActivityReactionKey(reaction) || ACTIVITY_REACTION_DEFAULT;
  const eventKey = getActivityEventKey(event);
  const commentKey = getActivityCommentKey(comment);
  const nodeKey = String(node?.key || '').trim();
  const userKey = String(user?.key || '').trim();
  if (!nodeKey || !userKey) return;
  if (!eventKey && !commentKey) return;

  const existingKey = String(existing?.key || '').trim();
  const existingReaction = reactionKeyForRow(existing);
  const legacyLikeKey = String(legacyLike?.key || '').trim();
  const existingIsCurrent = existing?.type === EngagementType.ACTIVITY_REACTION
    || (existingKey && existing?.type !== EngagementType.COMMENT_LIKE && existingReaction);

  if (existingKey && existingReaction === next) {
    await deleteEngagementByKey(existingKey);
    if (legacyLikeKey && legacyLikeKey !== existingKey) {
      await deleteEngagementByKey(legacyLikeKey);
    }
    return;
  }

  if (existingKey && existingIsCurrent) {
    await updateEngagement(existingKey, { reaction: next });
    if (legacyLikeKey && legacyLikeKey !== existingKey) {
      await deleteEngagementByKey(legacyLikeKey);
    }
    return;
  }

  if (existingKey) {
    await deleteEngagementByKey(existingKey);
  }
  if (legacyLikeKey && legacyLikeKey !== existingKey) {
    await deleteEngagementByKey(legacyLikeKey);
  }

  const key = generateKey();
  const resolvedBaseKey = resolveBaseKey(node) || String(node?.baseKey || '').trim();
  const payload = {
    key,
    type: EngagementType.ACTIVITY_REACTION,
    itemKey: nodeKey,
    reaction: next,
    userKey,
    user: buildUserSummary(user),
    dateCreated: nowTimestamp(),
  };

  const targetKey = eventKey
    ? getActivityReactionTargetKey(event, node)
    : getActivityCommentTargetKey(comment, node);
  if (targetKey) payload.targetKey = targetKey;

  if (eventKey) {
    payload.eventKey = eventKey;
  } else {
    payload.commentKey = commentKey;
  }

  if (resolvedBaseKey) {
    payload.baseKey = resolvedBaseKey;
    payload.itemSummary = withBaseKey({
      key: nodeKey,
      title: String(node?.title || ''),
      type: String(node?.type || ''),
    }, resolvedBaseKey);
  }

  await createEngagement({ key, engagement: payload });
}
